import { formatTimeDifference } from '../../formatting/timeFormatting';
import dayjs from '../../dayjsConfig';

export const calculateTotalHours = (shifts) =>
  Math.round(
    shifts.reduce((total, shift) => {
      if (shift.start && shift.end) {
        return total + (new Date(shift.end) - new Date(shift.start));
      }
      return total;
    }, 0) /
      (1000 * 60 * 60)
  );

/**
 * Formats a duration in minutes into a human-readable string
 * @param {number} minutes - The number of minutes
 * @returns {string} - Formatted string (e.g. "29m", "2h", or "2h 30m")
 */
export const formatDuration = (minutes) => {
  if (!minutes) return '0m';

  const wholeHours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  // If no hours, just show minutes
  if (wholeHours === 0) {
    return `${remainingMinutes}m`;
  }

  // If no remaining minutes, just show hours
  if (remainingMinutes === 0) {
    return `${wholeHours}h`;
  }

  // Show both hours and minutes
  return `${wholeHours}h ${remainingMinutes}m`;
};

// This function normalizes a date to midnight UTC
export const normalizeToMidnightUTC = (date) => {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0); // Force to midnight in UTC
  return d.getTime();
};

/**
 * Organizes shifts by day for efficient access
 * @param {Array} shifts - List of shifts
 * @param {Array} daysOfWeek - List of days of the week, starting with the venue's start of week
 * @returns {Object} - Map of shifts organized by day
 */
export const organizeShiftsByDay = (shifts, daysOfWeek, venueTimezone) => {
  if (!shifts || !shifts.length || !daysOfWeek || !daysOfWeek.length) {
    console.error('Missing shifts or days of week', {
      shiftsCount: shifts?.length || 0,
      daysCount: daysOfWeek?.length || 0,
    });
    return {};
  }

  if (!venueTimezone || typeof venueTimezone !== 'string') {
    console.error('Invalid venue timezone', venueTimezone);
    return {};
  }

  // Create a map where the keys are formatted days and the values are arrays of shifts
  const shiftMap = {};

  // Initialize the map with all days of the week
  daysOfWeek.forEach((dayObj) => {
    const dayAbbreviation = dayObj.day;
    shiftMap[dayAbbreviation] = [];
  });

  // Assign each shift to the corresponding day
  shifts.forEach((shift) => {
    const shiftStartVenueTimezone = dayjs.utc(shift.start).tz(venueTimezone);
    const shiftDayAbbreviation = shiftStartVenueTimezone.format('ddd');

    // Find the corresponding day of the week for this shift
    for (const dayObj of daysOfWeek) {
      // If the shift matches this day of the week, add it to the array
      const dayAbbreviation = dayObj.day;
      if (shiftDayAbbreviation === dayAbbreviation) {
        shiftMap[dayAbbreviation].push(shift);
        break; // Once assigned, no need to continue searching
      }
    }
  });

  return shiftMap;
};

/**
 * Returns the formatted scheduled label for a shift.
 * @param {string} ShiftType - The type of shift ('start' or 'end').
 * @param {object} shift - The shift object containing start, end, clockedInAt, and clockedOutAt.
 * @param {object} shiftsTypes - An object defining shift type constants.
 * @returns {string} - The formatted scheduled label or an empty string.
 */
export const getScheduledEndLabel = (ShiftType, shift, shiftsTypes, formatTime) => {
  return ShiftType === shiftsTypes.END && shift.clockedOutAt && shift.end && shift.scheduled
    ? `Scheduled ${formatTime(shift.end)}`
    : '';
};

/**
 * Determines whether the time picker should be open.
 */
export const shouldOpenPicker = (isStartTime, shift, openTimePicker, setOpenPicker, shiftsTypes) => {
  const { clockedInAt, clockedOutAt } = shift || {};

  if (shift.assignedName && isStartTime && clockedInAt) {
    setOpenPicker(false);
    return false;
  }

  if (!openTimePicker && !shift.assignedName && !isStartTime && clockedOutAt) {
    setOpenPicker(false);
    return false;
  }

  return openTimePicker;
};

/**
 * Creates a status object with text and status type, only when there's meaningful content to display
 * @param {boolean} isStartTime - Whether this is for start time or end time
 * @param {object} shift - The shift object
 * @returns {Object|null} Object with text and status, or null if no subtitle needed
 */
export const getShiftSubtitle = (isStartTime, shift, formatTime = null) => {
  const scheduledTime = isStartTime ? shift.start : shift.end;
  const actualTime = isStartTime ? shift.clockedInAt : shift.clockedOutAt;

  if (!shift || !shift.assignedName) {
    return null;
  }

  const now = new Date();

  // Determine the shift state
  let shiftTimeState = 'default';
  if (!shift.scheduled) {
    shiftTimeState = 'unscheduled';
  } else if (scheduledTime > now && !actualTime) {
    shiftTimeState = 'upcoming';
    // No state for missed times; those are displayed elsewhere
  }

  switch (shiftTimeState) {
    case 'unscheduled':
      return { shiftSubtitle: 'Unscheduled', status: 'info' };
    case 'upcoming':
      return {
        shiftSubtitle: formatTimeDifference(scheduledTime),
        status: 'info',
      };
    default:
      return {
        shiftSubtitle: `Scheduled ${formatTime(scheduledTime)}`,
        status: 'default',
      };
  }
};
